import { isSameDay } from 'date-fns';
import deepEqual from 'fast-deep-equal';

import { generateUtilityClasses } from '@kc-console/common-ui';

import type {
  CollectionFilterDef,
  DateFilterDef,
  DateRangeFilterDef,
  DateTimeRangeFilterDef,
  FilterDefBase,
  FilterState,
  PropertyFilterDef,
  PropertyFilterSelectOption,
  PropertyFilterTextOption,
  SegmentFilterDef,
  SwitchFilterDef,
  TextFilterDef,
} from './types';

function parseFilterDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }

  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function areDateValuesEqual(value: unknown, initialValue: unknown, includeTime: boolean): boolean {
  const date = parseFilterDate(value);
  const initialDate = parseFilterDate(initialValue);

  if (!date || !initialDate) {
    return deepEqual(value, initialValue);
  }

  return includeTime ? date.getTime() === initialDate.getTime() : isSameDay(date, initialDate);
}

function areDateRangeValuesEqual(
  value: unknown,
  initialValue: unknown,
  includeTime: boolean,
): boolean {
  if (
    !Array.isArray(value) ||
    value.length !== 2 ||
    !Array.isArray(initialValue) ||
    initialValue.length !== 2
  ) {
    return deepEqual(value, initialValue);
  }

  return (
    areDateValuesEqual(value[0], initialValue[0], includeTime) &&
    areDateValuesEqual(value[1], initialValue[1], includeTime)
  );
}

function areFilterValuesEqual(
  value: unknown,
  initialValue: unknown,
  filterDef: FilterDefBase | undefined,
): boolean {
  if (filterDef?.type === 'date') {
    return areDateValuesEqual(value, initialValue, false);
  }

  if (filterDef?.type === 'dateRange') {
    return areDateRangeValuesEqual(value, initialValue, false);
  }

  if (filterDef?.type === 'dateTimeRange') {
    return areDateRangeValuesEqual(value, initialValue, true);
  }

  return deepEqual(value, initialValue);
}

export const dataTableClasses = generateUtilityClasses('DataTable', [
  'root',
  'actionBar',
  'table',
  'tableContainer',
  'disabledRow',
  'selectableRow',
]);

export const createBuiltinColumnVariableKeys = (
  builtin: 'selection' | 'expand' | 'autoPadRight',
) => {
  return {
    size: `--data-table-builtin-column-${builtin}-size`,
    offset: `--data-table-builtin-column-${builtin}-offset`,
  } as const;
};
export const createColumnVariableKeys = (columnId: string) => {
  const safeColumnId = columnId.replaceAll(' ', '_').replaceAll('.', '_');
  return {
    isResizing: `--data-table-column-${safeColumnId}-is-resizing`,
    offset: `--data-table-header-${safeColumnId}-offset`,
    size: `--data-table-header-${safeColumnId}-size`,
  } as const;
};

export function isPropertyFilterDef(item: FilterDefBase): item is PropertyFilterDef {
  return item.type === 'property';
}

export function isTextFilterDef(item: FilterDefBase): item is TextFilterDef {
  return item.type === 'text';
}

// FIXME: property option인 경우, text 처리에 대한 탐지가 필요함.
export function isTextFilterOptionDef(item: FilterDefBase): item is PropertyFilterTextOption {
  return item.type === 'text';
}

export function isSelectFilterOptionDef(item: FilterDefBase): item is PropertyFilterSelectOption {
  return item.type === 'select';
}

export function isCollectionFilterDef(item: FilterDefBase): item is CollectionFilterDef {
  return item.type === 'collection';
}

export function isDateFilterDef(item: FilterDefBase): item is DateFilterDef {
  return item.type === 'date';
}

export function isDateRangeFilterDef(item: FilterDefBase): item is DateRangeFilterDef {
  return item.type === 'dateRange';
}

export function isDateTimeRangeFilterDef(item: FilterDefBase): item is DateTimeRangeFilterDef {
  return item.type === 'dateTimeRange';
}

export function isSwitchFilterDef(item: FilterDefBase): item is SwitchFilterDef {
  return item.type === 'switch';
}

export function isSegmentFilterDef(item: FilterDefBase): item is SegmentFilterDef {
  return item.type === 'segment';
}

export function getFilterDefMap(filterDef: readonly FilterDefBase[]) {
  const flatList = filterDef.reduce<FilterDefBase[]>((acc, item) => {
    if (isPropertyFilterDef(item)) {
      return [...acc, ...item.options];
    }
    return [...acc, item];
  }, []);

  return flatList.reduce<Record<string, FilterDefBase>>((acc, item) => {
    if (!item.id) {
      return acc;
    }

    return {
      ...acc,
      [item.id]: item,
    };
  }, {});
}

/**
 * 현재 필터가 초기 기준값과 의미적으로 다른지 확인합니다.
 * @description
 * - 필터 등록 순서는 무시합니다.
 * - date/dateRange는 달력 날짜, dateTimeRange는 시각까지 비교합니다.
 * - initialFilters가 없으면 빈 필터 목록을 기준값으로 사용합니다.
 */
export function getIsFilterApplied(
  filters: readonly FilterState[],
  initialFilters: readonly FilterState[] = [],
  filterDefs: readonly FilterDefBase[] = [],
): boolean {
  if (filters.length !== initialFilters.length) {
    return true;
  }

  const filterDefMap = getFilterDefMap(filterDefs);
  const unmatchedInitialFilters = [...initialFilters];

  for (const filter of filters) {
    const initialFilterIndex = unmatchedInitialFilters.findIndex(
      initialFilter =>
        initialFilter.key === filter.key &&
        areFilterValuesEqual(filter.value, initialFilter.value, filterDefMap[filter.key]),
    );

    if (initialFilterIndex === -1) {
      return true;
    }

    unmatchedInitialFilters.splice(initialFilterIndex, 1);
  }

  return false;
}
